import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';
import { Card, Container, Input } from 'reactstrap';

import { useBoolean } from 'usehooks-ts';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import DataTableInternalAccount from 'components/App/DataTableInternalAccount';
import Meta from 'components/App/Meta';
import ModalAddInternalAccount from 'components/Modals/ModalAddInternalAccount';
import Pagination from 'components/Pagination';
import SearchIcon from 'assets/images/search-icon.svg';

import { getParamsFormObject } from 'utils';

import AddIcon from 'assets/images/add-white.svg';

import styles from './ManagerInternalAccount.module.scss';

const breadcrumbList = [
  {
    name: 'Quản lý tài khoản',
    isActive: false,
    link: '',
  },
  {
    name: 'Tài khoản nội bộ',
    isActive: true,
    link: '',
  },
];

const DEFAULT_LIMIT = 10;

function ManagerInternalAccount(props) {
  const {
    marketList,
    marketTotal,
    isLoadingMarketList,
    actionGetMarketList,

    internalAccountList,
    internalAccountTotal,
    isLoadingGetInternalAccountList,
    actionGetInternalAccountList,
    actionDeleteInternalAccount,
    isLoadingDeleteInternalAccount,
  } = props;
  const history = useHistory();
  const tableRef = React.useRef(null);

  const {
    value: isOpenAddInternalAccountModal,
    setTrue: onOpenAddInternalAccountModal,
    setFalse: onCloseAddInternalAccountModal,
  } = useBoolean(false);

  const [editAccountId, setEditAccountId] = React.useState(null);

  const onToggleAddInternalAccountModal = React.useCallback(() => {
    if (isOpenAddInternalAccountModal) {
      onCloseAddInternalAccountModal();
      setEditAccountId(null);
    } else {
      onOpenAddInternalAccountModal();
    }
  }, [isOpenAddInternalAccountModal, onCloseAddInternalAccountModal,
    onOpenAddInternalAccountModal]);

  const searchQueries = React.useMemo(() => {
    const params = new URLSearchParams(history.location.search);

    return {
      page: Number(params.get('page') || 1),
      q: params.get('q') || '',
    };
  }, [history.location.search]);

  const pageCount = React.useMemo(
    () => Math.ceil((internalAccountTotal || 0) / DEFAULT_LIMIT) || 0,
    [internalAccountTotal],
  );

  const onRefreshList = React.useCallback(() => {
    const payload = {
      page: searchQueries.page,
      limit: DEFAULT_LIMIT,
    };

    if (searchQueries.q) {
      payload.q = searchQueries.q;
    }

    actionGetInternalAccountList(payload);
  }, [actionGetInternalAccountList, searchQueries]);

  React.useEffect(() => {
    onRefreshList();
  }, [onRefreshList]);

  const onDeleteSuccess = React.useCallback(() => {
    if (searchQueries.page === 1) {
      onRefreshList();
    } else {
      history.push({
        search: getParamsFormObject({
          q: searchQueries.q,
          page: 1,
        }),
      });
    }
  }, [history, searchQueries, onRefreshList]);

  const onChangePage = React.useCallback(
    (selected) => {
      history.push({
        search: getParamsFormObject({
          q: searchQueries.q,
          page: selected.selected + 1,
        }),
      });

      tableRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
    [history, searchQueries.q],
  );

  const debouncedUpdateSearchQuery = React.useMemo(
    () => debounce((keyword) => {
      history.push({
        search: getParamsFormObject({
          q: keyword,
          page: 1,
        }),
      });
    }, 500),
    [history],
  );

  const onSearch = React.useCallback((event) => {
    debouncedUpdateSearchQuery(event.target.value);
  }, [debouncedUpdateSearchQuery]);

  const onAddAccount = React.useCallback(() => {
    onOpenAddInternalAccountModal();
  }, [onOpenAddInternalAccountModal]);

  const onEditAccount = React.useCallback((id) => {
    setEditAccountId(id);
    onOpenAddInternalAccountModal();
  }, [onOpenAddInternalAccountModal]);

  React.useEffect(
    () => () => debouncedUpdateSearchQuery.cancel(),
    [debouncedUpdateSearchQuery],
  );

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name="Tài khoản nội bộ" />

      <Container fluid>
        <Breadcrumb
          pageTitle="TÀI KHOẢN NỘI BỘ"
          breadcrumbList={breadcrumbList}
        />

        <Card className="Section">
          <div className="Section__Search">
            <div className="Section__Search__Container">
              <Input
                placeholder="Tìm theo tên, số điện thoại, địa chỉ email"
                className="Section__Search__Input"
                defaultValue={searchQueries.q}
                onChange={onSearch}
              />

              <img
                src={SearchIcon}
                alt="search"
                className="Section__Search__Icon__Search"
              />
            </div>

            <div className="Button__Add__Wrapper">
              <Button
                classes="Button__Add"
                fontSize="13px"
                onClick={onAddAccount}
              >
                <img src={AddIcon} alt="add" />
                Thêm tài khoản
              </Button>
            </div>
          </div>

          <div ref={tableRef}>
            <DataTableInternalAccount
              isLoading={isLoadingGetInternalAccountList}
              data={internalAccountList}
              onEdit={onEditAccount}
              actionDeleteInternalAccount={actionDeleteInternalAccount}
              isLoadingDeleteInternalAccount={isLoadingDeleteInternalAccount}
              onDeleteSuccess={onDeleteSuccess}
            />
          </div>
        </Card>

        {internalAccountTotal > 0 && (
          <Pagination
            page={searchQueries.page}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        )}
      </Container>

      <ModalAddInternalAccount
        isOpen={isOpenAddInternalAccountModal}
        onToggleModal={onToggleAddInternalAccountModal}
        marketList={marketList}
        marketTotal={marketTotal}
        isLoadingMarketList={isLoadingMarketList}
        actionGetMarketList={actionGetMarketList}
        editAccountId={editAccountId}
        onRefreshList={onRefreshList}
      />
    </div>
  );
}

ManagerInternalAccount.propTypes = {
  marketList: PropTypes.instanceOf(Array),
  marketTotal: PropTypes.number,
  isLoadingMarketList: PropTypes.bool,
  actionGetMarketList: PropTypes.func,

  internalAccountList: PropTypes.instanceOf(Array),
  internalAccountTotal: PropTypes.number,
  isLoadingGetInternalAccountList: PropTypes.bool,
  actionGetInternalAccountList: PropTypes.func,
  actionDeleteInternalAccount: PropTypes.func,
  isLoadingDeleteInternalAccount: PropTypes.bool,
};

ManagerInternalAccount.defaultProps = {
  marketList: [],
  marketTotal: 0,
  isLoadingMarketList: false,
  actionGetMarketList: () => {},

  internalAccountList: [],
  internalAccountTotal: 0,
  isLoadingGetInternalAccountList: false,
  actionGetInternalAccountList: () => {},
  actionDeleteInternalAccount: () => {},
  isLoadingDeleteInternalAccount: false,
};

export default ManagerInternalAccount;
